<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">{{ supplierData?.id ? $t('supplierEditModalTitle') : $t('supplierCreateModalTitle') }}</div>
    </template>
    <Spinner v-show="dataIsSending"/>
    <div v-show="!dataIsSending" class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5" v-if="$store.getters.isLocationInEstonia">
        <EECompanyRegisterDropdown v-model="selectedCompany"
                                   :disabled="dataIsSending"
                                   :resetOnClose="true"
                                   :submitted="submitted"
                                   :required="true"
                                   :visible="visible"
                                   :showClear="true"
                                   @change="changeCompany"
                                   inputId="supplier-modal-ee-company-dropdown"/>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="company_name" :class="{'p-invalid' : submitted && !supplierData.company_name}" v-model.trim="supplierData.company_name" autocomplete="new-password"/>
          <label for="company_name">{{ $t('Company name') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !supplierData.company_name">{{ $t('Required field') }}</small>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="supplier_name" v-model.trim="supplierData.name" autocomplete="new-password"/>
          <label for="supplier_name">{{ $t('Supplier name') }}</label>
        </div>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="supplier_reg_number" :class="{'p-invalid' : submitted && !supplierData.reg_number}" v-model.trim="supplierData.reg_number" autocomplete="new-password"/>
          <label for="supplier_reg_number">{{ $t('Reg. number') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !supplierData.reg_number">{{ $t('Required field') }}</small>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="supplier_vat_number" v-model.trim="supplierData.vat_number" autocomplete="new-password"/>
          <label for="supplier_vat_number">{{ $t('VAT number') }}</label>
        </div>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <PhoneInput v-model:phoneCountry="supplierData.phone_country"
                    v-model:phoneCode="supplierData.phone_code"
                    v-model:phoneNumber="supplierData.phone_number"
                    v-model:isInvalid="supplierPhoneInvalid"
                    :modalData="supplierData"
                    :visible="visible"
                    :submitted="submitted"/>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <EmailInput v-model="supplierData.email"
                    v-model:isInvalid="emailInvalid"
                    :submitted="submitted"
                    inputId="supplier-email"/>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="supplier_bank_name" v-model.trim="supplierData.bank_name" autocomplete="new-password"/>
          <label for="supplier_bank_name">{{ $t('Bank name') }}</label>
        </div>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputText id="supplier_bank_account" v-model.trim="supplierData.bank_account" autocomplete="new-password"/>
          <label for="supplier_bank_account">{{ $t('Bank account') }}</label>
        </div>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputNumber :locale="computedNumberInputLocale" id="supplier_days_to_pay" v-model="supplierData.days_to_pay" :min="0" :max="60" showButtons autocomplete="new-password"/>
          <label for="supplier_days_to_pay">{{ $t('Days to pay') }}</label>
        </div>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="supplier-tax"
                          data-key="id"
                          v-model="selectedTax"
                          :options="taxesData"
                          :loading="taxesInFetch"
                          :filter="true"
                          :filterFields="['name', 'value']"
                          :showClear="true">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span>{{slotProps.value.value}}% <span v-if="slotProps.value.name">({{ slotProps.value.name }})</span></span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div>
                <span>{{slotProps.option.value}}% <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></span>
              </div>
            </template>
          </CustomDropdown>
          <label for="supplier-tax">{{ $t('Tax') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <LimitedCharsTextarea :submitted="submitted"
                              :rows="3"
                              v-model="supplierData.comment"
                              :maxChars="commentMaxChars"
                              :autocomplete="'new-password'"
                              :id="'supplier-modal-notes'"
                              :label="$t('Notes')"/>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-text" @click="close"/>
      <Button :label="$t('Save')"
              :disabled="dataIsSending || disableSaveButton"
              :loading="dataIsSending"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>
  <ItemExistsModal :visible="itemExistsModal"
                   :itemId="supplierData?.id ? +supplierData.id : null"
                   type="supplier"
                   :items="foundItems"
                   @select-existing="selectExistingItem"
                   @close="closeItemExistsModal"/>
</template>

<script>
import httpClient from '@/services/http.services'
import settings from "@/settings";
import formatMixins from "@/mixins/formatMixins";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import PhoneInput from "@/components/PhoneInput";
import EmailInput from "@/components/EmailInput";
import EECompanyRegisterDropdown from "@/components/EECompanyRegisterDropdown";
import httpMixins from "@/mixins/httpMixins";
import foundModalItemsMixin from "@/mixins/foundModalItemsMixin";
import ItemExistsModal from "@/components/ItemExistsModal.vue";

export default {
  components: { ItemExistsModal, PhoneInput, EmailInput, EECompanyRegisterDropdown },
  mixins: [ showErrorsMixins, formatMixins, httpMixins, foundModalItemsMixin ],
  emits: ['close', 'update-items', 'update-item'],
  name: 'SupplierModal',
  props: {
    item: {
      type: Object,
      default: null,
    },
    itemAddress: {
      type: Object,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    modalLayer: {
      type: Number,
      default: 1
    },
    taxes: {
      type: [Array, null],
      default: null
    },
  },
  data() {
    return {
      taxesData: [],
      emailInvalid: false,
      supplierPhoneInvalid: false,

      selectedCompany: null,
      commentMaxChars: 500,
      dataIsSending: false,
      disableSaveButton: false,
      supplierData: {},
      submitted: false,
      selectedTax: null,
      // emailIsValid: false,
    }
  },
  watch: {
    item(value) {
      this.createItemData(value)
    },
    visible() {
      if (!this.visible) {
        this.disableSaveButton = false
        this.dataIsSending = false
        this.submitted = false
        this.selectedTax = null
      } else {
        if (this.taxes) {
          // console.log('no')
          this.taxesData = this.taxes
          this.setSelectedTax()
        } else {
          this.getTaxes().then(() => this.setSelectedTax())
        }
      }
    },
  },
  methods: {
    setSelectedTax() {
      const taxId = this.supplierData?.tax_id
      this.selectedTax = taxId != null
          ? (this.taxesData?.find(t => +t.id === +taxId) ?? null)
          : null
    },
    changeCompany() {
      if (!this.selectedCompany) {
        this.supplierData.company_name = null
        this.supplierData.reg_number = null
        this.supplierData.vat_number = null
      } else {
        this.supplierData.company_name = this.selectedCompany.name
        this.supplierData.reg_number = this.selectedCompany.registry_code
        this.supplierData.vat_number = this.selectedCompany.vat
      }
    },
    // createItemData(value) {
    //   if (!value) return
    //   this.supplierData = { ...value }
    //   if (value.email) {
    //     this.validateEmail(value.email)
    //   }
    // },
    createItemData(value) {
      this.supplierData = value ? { ...value } : {}
      this.setSelectedTax()
      this.selectedCompany = null
      this.emailInvalid = false
      this.supplierPhoneInvalid = false

      // if (value?.email) {
      //   this.validateEmail(value.email)
      // }
    },
    async saveItem() {
      this.submitted = true
      if (!this.supplierData ||
          !this.supplierData.company_name ||
          !this.supplierData.reg_number ||
          this.emailInvalid ||
          this.supplierPhoneInvalid ||
          this.supplierData.comment?.length > this.commentMaxChars) {
        return false
      }

      this.disableSaveButton = true
      this.dataIsSending = true

      // const savePhoneNumber = this.supplierData.phone_country && this.supplierData.phone_code && this.supplierData.phone_number
      const savePhoneNumber = this.supplierData.phone_code && this.supplierData.phone_number

      const supplier = {
        id: this.supplierData.id ?? null,
        company_name: this.supplierData.company_name ? this.supplierData.company_name.trim() : null,
        name: this.supplierData.name ? this.supplierData.name.trim() : null,
        reg_number: this.supplierData.reg_number ? this.supplierData.reg_number.trim() : null,
        vat_number: this.supplierData.vat_number ? this.supplierData.vat_number.trim() : null,
        bank_name: this.supplierData.bank_name ? this.supplierData.bank_name.trim() : null,
        bank_account: this.supplierData.bank_account ? this.supplierData.bank_account.trim() : null,
        email: this.supplierData.email ? this.supplierData.email.trim() : null,
        phone_country: savePhoneNumber ? this.supplierData.phone_country : null,
        phone_code: savePhoneNumber ? this.supplierData.phone_code : null,
        phone_number: savePhoneNumber ? this.supplierData.phone_number : null,
        days_to_pay: this.supplierData.days_to_pay ?? null,
        tax_id: this.selectedTax?.id ?? null,
        comment: this.supplierData.comment ? this.supplierData.comment.trim() : null,
        isNew: false,
      }

      if (this.supplierData.id) {
        try {
          const { status, data } = await httpClient.post(`supplier/update`, supplier)
          if (status === 200 && data?.success) {
            this.supplierData.isNew = false
            this.$emit('update-items', this.supplierData.id)
            this.$emit('update-item', supplier)
            this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife});
            this.close()
          } else if (data?.found_items) {
            this.foundItems = data.found_items
            this.openItemExistsModal()
          } else if (data?.error) {
            if (data.error.detail === 'Registration number has already been taken.') {
              this.$toast.add({severity:'error', summary: this.$t('Error'), detail: this.$t('registrationNumberTaken'), life: settings.toastLife})
            } else {
              this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: settings.toastLife});
            }
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
        }
      } else {
        try {
          const { status, data } = await httpClient.post(`supplier/create`, supplier)
          if (status === 201 && data?.success) {
            this.supplierData.id = data.id
            supplier.id = data.id
            supplier.isNew = true
            this.$emit('update-items', this.supplierData.id)
            this.$emit('update-item', supplier)
            this.$toast.add({severity:'success', detail: this.$t('Data saved'), life: settings.toastLife});
            this.close()
          } else if (data?.found_items) {
            this.foundItems = data.found_items
            this.openItemExistsModal()
          } else if (data?.error) {
            if (data.error.detail === 'Registration number has already been taken.') {
              this.$toast.add({severity:'error', summary: this.$t('Error'), detail: this.$t('registrationNumberTaken'), life: settings.toastLife})
            } else {
              this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: settings.toastLife});
            }
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
        }
      }
    },
    close() {
      this.$emit('close')
      this.submitted = false
    },
    // validateEmail(email) {
    //   const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    //   this.emailIsValid = re.test(email)
    // }
  },
  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, width: '95%', maxWidth: '550px', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, width: '550px', marginTop: '15px'}
      } else {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, width: '550px'}
      }
    },
  },

}
</script>